/* eslint-disable react/jsx-curly-newline */
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMediaQuery } from '@openedx/paragon';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { CheckoutTestResult } from 'components/ui/organisms/monarch/rules/checkout/types';
import { useCohesionConfig } from 'helpers/eventing';
import { MonarchDataConsumer } from 'helpers/eventing/monarch/MonarchDataConsumer';
import { getPriceDetailsFromCart } from 'helpers/priceHelpers';
import {
  getHasCartLoadFailure,
  getIsSessionInvalid,
  useAppSelector,
} from 'store';
import { useCart } from 'frontastic';
import { CheckoutProps } from '../types';
import { FreeOrderCheckoutForm } from './checkout-form/free-order-form';
import { OrderDetails } from './order-detail';
import { messages as orderDetailMessages } from './order-detail.messages';
import { ProductLineItem } from './product-line-item';
import { CheckoutSkeleton } from './skeletons';
import {
  Alert,
  CheckoutForm,
  EmptyCartCard,
  OrderSummary,
  SessionInvalidAlertModal,
  StripeFormProvider,
} from '.';
import './style.scss';

export const CheckoutTest = ({ stripePublishableKey }: CheckoutProps) => {
  const { formatMessage } = useIntl();
  const hasCartLoadFailure = useAppSelector(getHasCartLoadFailure);
  const isSessionInvalid = useAppSelector(getIsSessionInvalid);
  const { data, isEmpty, isLoading, redeemDiscountCode, removeDiscountCode } =
    useCart();

  const lineItems = data?.lineItems ?? [];
  const priceDetails = getPriceDetailsFromCart(data);
  const isCreditPurchase = lineItems.some(
    (item) => item.variant.attributes?.mode === 'credit',
  );

  const stripeOptions = {
    mode: 'payment',
    amount: priceDetails.cartTotal,
    currency: priceDetails.currencyCode?.toLowerCase(),
    paymentMethodCreation: 'manual',
    paymentMethodTypes: ['card'],
  } satisfies StripeElementsOptions;

  const isEmptyCart = isEmpty || lineItems.length === 0;
  const isFreeOrder = priceDetails.cartTotal === 0;
  const shouldHideAlert = isSessionInvalid && !hasCartLoadFailure;
  const purchaseType = lineItems.some(({ bundleId }) => bundleId)
    ? 'program'
    : lineItems[0]?.variant?.attributes?.mode;
  const orderDetailHeadingID = 'payment.order.details.section.heading';
  const orderDetailMessageID =
    purchaseType === 'program'
      ? 'payment.order.details.section.mode.program'
      : purchaseType === 'professional'
        ? 'payment.order.details.section.mode.professional'
        : purchaseType === 'credit'
          ? 'payment.order.details.section.mode.credit'
          : 'payment.order.details.section.mode.verified';

  useCohesionConfig('checkout');

  const isMobile = useMediaQuery({ maxWidth: 820 });

  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#F9F8F6';
    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);

  const renderCheckout = () => (
    <div className="checkout-container checkout-test-container">
      <div className="row responsive-row">
        {/* LEFT COLUMN: Checkout Form */}
        <div
          className="col-lg-7 col-md-12 mb-4 form-column-styling"
          style={{
            backgroundColor: '#F9F8F6',
            padding: '3.5rem 2rem 2rem 2rem',
          }}
        >
          <h1 style={{ marginBottom: '32px', fontSize: '2.75rem' }}>
            <MonarchDataConsumer<CheckoutTestResult> experimentKey="checkout">
              {(monarchFormData) =>
                monarchFormData?.data?.formTitleText ?? (
                  <FormattedMessage
                    id="checkout.title"
                    defaultMessage="Checkout"
                    description="Checkout page title"
                  />
                )
              }
            </MonarchDataConsumer>
          </h1>
          {!shouldHideAlert && <Alert />}
          {/* In your cart section for mobile */}
          {isMobile && (
            <>
              <div
                className="cart-section"
                style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '16px 24px 24px 24px',
                  marginBottom: '32px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                <h2 style={{ fontWeight: 700, fontSize: '1.25rem' }}>
                  <MonarchDataConsumer<CheckoutTestResult> experimentKey="checkout">
                    {(monarchCartData) =>
                      monarchCartData?.data?.cartTitleText ?? (
                        <FormattedMessage
                          id="checkout.cart.title"
                          defaultMessage="In your cart"
                          description="Cart section title"
                        />
                      )
                    }
                  </MonarchDataConsumer>
                </h2>
                <p style={{ fontWeight: 400, fontSize: '1rem' }}>
                  <FormattedMessage
                    id="checkout.cart.description"
                    defaultMessage="Your purchase contains the following:"
                    description="Cart section description"
                  />
                </p>
                {lineItems.map((item) => (
                  <ProductLineItem key={item.lineItemId} item={item} />
                ))}
              </div>
              <div style={{ margin: '32px 0' }}>
                <OrderSummary
                  isCreditPurchase={isCreditPurchase}
                  priceDetails={priceDetails}
                  onApplyCoupon={redeemDiscountCode(data!.cartId!)}
                  onRemoveCoupon={removeDiscountCode(data!.cartId!)}
                />
              </div>
            </>
          )}
          {!isFreeOrder && (
            <StripeFormProvider
              stripePublishableKey={stripePublishableKey}
              options={stripeOptions}
            >
              {isMobile ? (
                <CheckoutForm
                  cartID={data!.cartId!}
                  priceDetails={priceDetails}
                  lineItems={lineItems}
                  showPlaceOrderButton
                  showPayPalSection
                  isMobile={isMobile}
                />
              ) : (
                <CheckoutForm
                  cartID={data!.cartId!}
                  priceDetails={priceDetails}
                  lineItems={lineItems}
                />
              )}
            </StripeFormProvider>
          )}
          {isFreeOrder && !isMobile && (
            <FreeOrderCheckoutForm
              cartID={data!.cartId!}
              priceDetails={priceDetails}
              lineItems={lineItems}
            />
          )}
          {isFreeOrder && isMobile && (
            <>
              <div style={{ margin: '32px 0' }}>
                <OrderDetails purchaseType={purchaseType} />
              </div>
              <FreeOrderCheckoutForm
                cartID={data!.cartId!}
                priceDetails={priceDetails}
                lineItems={lineItems}
              />
            </>
          )}
          {/* Mobile: Place Order, Summary, PayPal, Order Details */}
          {isMobile && !isFreeOrder && (
            <div style={{ margin: '32px 0' }}>
              <OrderDetails purchaseType={purchaseType} />
            </div>
          )}
        </div>
        {/* RIGHT COLUMN: Order Summary */}
        {!isMobile && (
          <div
            className="col-lg-5 col-md-12 summary-column-container"
            style={{
              background: '#F3F1ED',
              paddingLeft: '3rem',
              paddingTop: '3.5rem',
              paddingBottom: '5rem',
              minHeight: '100vh',
            }}
          >
            <div style={{ maxWidth: '475px', width: '100%' }}>
              {/* "In your cart" section */}
              <div
                className="cart-section w-100"
                style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '32px',
                }}
              >
                <h2 className="cart-section-title">
                  <MonarchDataConsumer<CheckoutTestResult> experimentKey="checkout">
                    {(monarchCartData2) =>
                      monarchCartData2?.data?.cartTitleText ?? (
                        <FormattedMessage
                          id="checkout.cart.title"
                          defaultMessage="In your cart"
                          description="Cart section title"
                        />
                      )
                    }
                  </MonarchDataConsumer>
                </h2>
                <p className="cart-section-desc">
                  <FormattedMessage
                    id="checkout.cart.description"
                    defaultMessage="Your purchase contains the following:"
                    description="Cart section description"
                  />
                </p>
                {lineItems.map((item) => (
                  <ProductLineItem key={item.lineItemId} item={item} />
                ))}
              </div>
              {/* OrderSummary component now directly under summary-column-container */}
              <OrderSummary
                isCreditPurchase={isCreditPurchase}
                priceDetails={priceDetails}
                onApplyCoupon={redeemDiscountCode(data!.cartId!)}
                onRemoveCoupon={removeDiscountCode(data!.cartId!)}
              />
              {/* Order details */}
              <div className="order-details-desktop w-100">
                <h2 className="h4 mt-5 mb-3">
                  {formatMessage(orderDetailMessages[orderDetailHeadingID])}
                </h2>
                <p className="mb-0 order-details-note">
                  {formatMessage(orderDetailMessages[orderDetailMessageID])}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return <CheckoutSkeleton />;
  }

  return (
    <>
      <SessionInvalidAlertModal isOpen={isSessionInvalid} />
      {hasCartLoadFailure ? null : isEmptyCart ? (
        <EmptyCartCard />
      ) : (
        renderCheckout()
      )}
    </>
  );
};
